export const flattenObject = (
  obj: any,
  prefix = '',
  delimiter = '.',
): { [key: string]: any } => {
  const flattened: { [key: string]: any } = {}

  // Handle null/undefined at the root
  if (obj === null || obj === undefined) {
    return prefix ? { [prefix]: obj } : {}
  }

  // Handle primitives
  if (typeof obj !== 'object') {
    return prefix ? { [prefix]: obj } : { '': obj }
  }

  // Handle arrays with index syntax
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const arrayKey = prefix ? `${prefix}[${index}]` : `[${index}]`
      Object.assign(flattened, flattenObject(item, arrayKey, delimiter))
    })

    // Also store array length for reconstruction
    if (prefix) {
      flattened[`${prefix}._length`] = obj.length
    }

    return flattened
  }

  // Handle objects
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    const newKey = prefix ? `${prefix}${delimiter}${key}` : key

    if (value === null || value === undefined) {
      // Preserve null/undefined values
      flattened[newKey] = value
    } else if (Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, newKey, delimiter))
    } else if (typeof value === 'object') {
      Object.assign(flattened, flattenObject(value, newKey, delimiter))
    } else {
      flattened[newKey] = value
    }
  })

  return flattened
}

export const unflattenObject = (
  flattened: { [key: string]: any },
  delimiter = '.',
): any => {
  const result: any = {}

  // Sort keys to handle arrays properly (process array elements before length)
  const sortedKeys = Object.keys(flattened).sort((a, b) => {
    if (a.includes('[') && b.includes('[')) {
      return a.localeCompare(b, undefined, { numeric: true })
    }
    return a.localeCompare(b)
  })

  sortedKeys.forEach((key) => {
    const value = flattened[key]

    // Skip internal array length markers
    if (key.endsWith('._length')) return

    const path = parseKeyPath(key, delimiter)
    setNestedValue(result, path, value)
  })

  return result
}

// Public helper to safely read nested values using dot paths and bracket indices
export const getValueAtPath = (
  source: unknown,
  path: string,
  delimiter = '.',
): unknown => {
  if (source === null || source === undefined) return undefined
  const tokens = parseKeyPath(path, delimiter)
  let curr: any = source
  for (const token of tokens) {
    if (curr === null || curr === undefined) return undefined
    curr = curr[token as any]
  }
  return curr
}

// Helper to parse key paths with array indices
const parseKeyPath = (
  key: string,
  delimiter: string,
): Array<string | number> => {
  const path: Array<string | number> = []
  let current = ''
  let i = 0

  while (i < key.length) {
    const char = key[i]

    if (char === '[') {
      // Push the current key part if it exists
      if (current) {
        path.push(current)
        current = ''
      }

      // Find the closing bracket and extract the index
      const closingBracket = key.indexOf(']', i)
      const index = Number.parseInt(key.slice(i + 1, closingBracket))
      path.push(index)
      i = closingBracket + 1

      // Skip the delimiter after the bracket
      if (key[i] === delimiter) i++
    } else if (char === delimiter) {
      if (current) {
        path.push(current)
        current = ''
      }
      i++
    } else {
      current += char
      i++
    }
  }

  if (current) {
    path.push(current)
  }

  return path
}

// Helper to set nested values with array support
const setNestedValue = (
  obj: any,
  path: Array<string | number>,
  value: any,
): void => {
  let current = obj

  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    const nextKey = path[i + 1]

    if (typeof nextKey === 'number') {
      // Next step expects an array container
      if (!Array.isArray(current[key])) {
        current[key] = []
      }
      current = current[key]
    } else {
      // Next step expects an object container (including when current is an array index)
      const child = current[key]
      if (typeof child !== 'object' || child === null || Array.isArray(child)) {
        current[key] = {}
      }
      current = current[key]
    }
  }

  const finalKey = path[path.length - 1]
  current[finalKey] = value
}
